import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Alert,
  CircularProgress,
  Button,
  AppBar,
  Toolbar,
  TextField,
  Stack,
  LinearProgress,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CheckCircle, Error as ErrorIcon, Lock } from '@mui/icons-material';
import { ROUTES } from '../config/constants';
import { authService } from '../services/authService';
import { trackEvent } from '../services/analytics';
import LanguageSwitcher from '../components/LanguageSwitcher';
import PublicPageFooter from '../components/PublicPageFooter';

type Stage =
  | 'checking'        // Preview the token before consuming
  | 'set-password'    // Backoffice contact — show password form
  | 'auto-activating' // Self-signup — fire and forget
  | 'success'
  | 'error';

interface CheckResult {
  email?: string;
  full_name?: string;
  requires_password?: boolean;
}

/** Match the server-side rule in app/api/v1/auth.py:ActivateRequest. */
function validatePassword(pw: string): string | null {
  if (pw.length < 12) return 'Password must be at least 12 characters.';
  if (!/[A-Za-z]/.test(pw)) return 'Password must contain at least one letter.';
  if (!/\d/.test(pw)) return 'Password must contain at least one digit.';
  return null;
}

/** Visual strength meter — purely cosmetic, server enforces the real rule. */
function passwordStrength(pw: string): { score: number; label: string } {
  if (!pw) return { score: 0, label: '' };
  let score = 0;
  if (pw.length >= 12) score += 25;
  if (pw.length >= 16) score += 15;
  if (/[A-Z]/.test(pw)) score += 15;
  if (/[a-z]/.test(pw)) score += 10;
  if (/\d/.test(pw)) score += 15;
  if (/[^A-Za-z0-9]/.test(pw)) score += 20;
  if (score > 100) score = 100;
  const label = score < 40 ? 'Weak' : score < 70 ? 'OK' : score < 90 ? 'Strong' : 'Excellent';
  return { score, label };
}

const ActivatePage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [stage, setStage] = useState<Stage>('checking');
  const [checkResult, setCheckResult] = useState<CheckResult>({});
  const [message, setMessage] = useState<string>('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const token = searchParams.get('token');

  const translateError = useCallback(
    (raw: string): string => {
      if (raw.includes('already been used')) return t('activation.alreadyUsed');
      if (raw.includes('expired')) return t('activation.expired');
      if (raw.toLowerCase().includes('invalid')) return t('activation.invalidToken');
      return raw;
    },
    [t]
  );

  // Step 1 — preview the token to learn whether a password is needed.
  useEffect(() => {
    if (!token) {
      setStage('error');
      setMessage(t('activation.invalidToken'));
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await authService.checkActivationToken(token);
        if (cancelled) return;
        // Happy path: the link was already consumed by this same user
        // (e.g. bfcache restored the page, or they reopened the link
        // after activating). Show success, not error.
        if (r.already_activated) {
          setCheckResult({ email: r.email, full_name: r.full_name, requires_password: false });
          setStage('success');
          setMessage(
            r.email
              ? `${r.email} is already activated. Please log in.`
              : t('activation.successMessage'),
          );
          return;
        }
        if (!r.valid) {
          setStage('error');
          setMessage(translateError(r.error || ''));
          return;
        }
        setCheckResult({
          email: r.email,
          full_name: r.full_name,
          requires_password: !!r.requires_password,
        });
        // Self-signup path: no password needed, just activate immediately.
        if (!r.requires_password) {
          setStage('auto-activating');
          try {
            const ar = await authService.activateAccount(token);
            if (cancelled) return;
            setStage('success');
            setMessage(ar.message || t('activation.successMessage'));
          } catch (e: any) {
            if (cancelled) return;
            setStage('error');
            setMessage(translateError(e?.response?.data?.detail ?? e?.message ?? ''));
          }
        } else {
          setStage('set-password');
        }
      } catch (e: any) {
        if (cancelled) return;
        setStage('error');
        setMessage(translateError(e?.response?.data?.detail ?? e?.message ?? ''));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, t, translateError]);

  // Step 2 (only for backoffice contacts) — submit the chosen password.
  const submitPassword = async () => {
    setFormError(null);
    const v = validatePassword(password);
    if (v) {
      setFormError(v);
      return;
    }
    if (password !== confirmPassword) {
      setFormError('Passwords do not match.');
      return;
    }
    if (!token) return;
    setSubmitting(true);
    try {
      const ar = await authService.activateAccount(token, password);
      setStage('success');
      setMessage(ar.message || t('activation.successMessage'));
      trackEvent('magic_link_activated');
    } catch (e: any) {
      setFormError(translateError(e?.response?.data?.detail ?? e?.message ?? ''));
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoToLogin = () => navigate(ROUTES.LOGIN);
  const handleGoToHome = () => navigate(ROUTES.HOME);
  const strength = passwordStrength(password);

  return (
    <Box>
      <AppBar position="static" sx={{ backgroundColor: '#1976d2' }}>
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Typography variant="h6" component="div">
            Mowilex Customer Portal
          </Typography>
          <LanguageSwitcher />
        </Toolbar>
      </AppBar>

      <Container maxWidth="sm">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 'calc(100vh - 64px)',
            py: 4,
          }}
        >
          <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
            <Typography variant="h4" component="h1" gutterBottom textAlign="center">
              {t('activation.title')}
            </Typography>

            {(stage === 'checking' || stage === 'auto-activating') && (
              <Box textAlign="center">
                <CircularProgress sx={{ mb: 2 }} />
                <Typography variant="body1" color="text.secondary">
                  {t('activation.loading')}
                </Typography>
              </Box>
            )}

            {stage === 'set-password' && (
              <Stack spacing={2} sx={{ mt: 2 }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Lock color="primary" />
                  <Typography variant="h6">{t('auth.setYourPassword')}</Typography>
                </Box>
                {checkResult.email && (
                  <Alert severity="info">
                    {t('auth.settingUpPasswordFor')} <strong>{checkResult.email}</strong>
                    {checkResult.full_name ? ` (${checkResult.full_name})` : ''}.
                  </Alert>
                )}
                {formError && <Alert severity="error">{formError}</Alert>}

                <TextField
                  label={t('auth.newPassword')}
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  fullWidth
                  autoFocus
                  autoComplete="new-password"
                  helperText="At least 12 characters with a letter and a digit."
                />
                {password.length > 0 && (
                  <Box>
                    <LinearProgress
                      variant="determinate"
                      value={strength.score}
                      color={
                        strength.score < 40
                          ? 'error'
                          : strength.score < 70
                            ? 'warning'
                            : 'success'
                      }
                    />
                    <Typography variant="caption" color="text.secondary">
                      Strength: {strength.label}
                    </Typography>
                  </Box>
                )}
                <TextField
                  label={t('auth.confirmPasswordShort')}
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  fullWidth
                  autoComplete="new-password"
                  error={confirmPassword.length > 0 && confirmPassword !== password}
                  helperText={
                    confirmPassword.length > 0 && confirmPassword !== password
                      ? 'Passwords do not match'
                      : ' '
                  }
                />

                <Button
                  variant="contained"
                  size="large"
                  onClick={submitPassword}
                  disabled={
                    submitting ||
                    !password ||
                    !confirmPassword ||
                    password !== confirmPassword
                  }
                  fullWidth
                >
                  {submitting ? <CircularProgress size={22} /> : 'Set password & activate'}
                </Button>
              </Stack>
            )}

            {stage === 'success' && (
              <Box textAlign="center">
                <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
                <Alert severity="success" sx={{ mb: 3 }}>
                  <Typography variant="body1">{t('activation.success')}</Typography>
                </Alert>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  {message}
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handleGoToLogin}
                  sx={{ mr: 2 }}
                >
                  {t('activation.goToLogin')}
                </Button>
                <Button variant="outlined" size="large" onClick={handleGoToHome}>
                  {t('activation.goToHome')}
                </Button>
              </Box>
            )}

            {stage === 'error' && (
              <Box textAlign="center">
                <ErrorIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
                <Alert severity="error" sx={{ mb: 3 }}>
                  <Typography variant="body1">{message}</Typography>
                </Alert>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  {t('activation.contactSupport')}
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handleGoToLogin}
                  sx={{ mr: 2 }}
                >
                  {t('activation.goToLogin')}
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => window.location.reload()}
                >
                  {t('activation.tryAgain')}
                </Button>
              </Box>
            )}
          </Paper>
        </Box>
        <PublicPageFooter />
      </Container>
    </Box>
  );
};

export default ActivatePage;
